<script setup lang="ts">
withDefaults(
  defineProps<{
    loading?: boolean;
  }>(),
  {
    loading: false,
  }
);

const FORMATS = ['DICOM', 'NIfTI', 'STL', 'ZIP'];
</script>

<template>
  <div class="pycad-welcome" v-bind="$attrs">
    <div class="pycad-welcome__dropzone" :class="{ 'is-loading': loading }">
      <template v-if="!loading">
        <div class="pycad-welcome__icon-badge">
          <v-icon size="34">mdi-cloud-upload-outline</v-icon>
        </div>

        <div class="pycad-welcome__title">Drop your medical images here</div>
        <div class="pycad-welcome__subtitle">
          or click anywhere to browse your files
        </div>

        <div class="pycad-welcome__cta">
          <v-icon size="16">mdi-folder-open-outline</v-icon>
          <span>Open files</span>
        </div>

        <div class="pycad-welcome__formats">
          <span
            v-for="format in FORMATS"
            :key="format"
            class="pycad-welcome__format-chip"
          >
            {{ format }}
          </span>
        </div>

        <div class="pycad-welcome__privacy">
          <v-icon size="13">mdi-shield-check-outline</v-icon>
          <span>Your data stays in your browser — nothing is uploaded.</span>
        </div>
      </template>

      <template v-else>
        <div class="pycad-welcome__icon-badge pycad-welcome__icon-badge--pulse">
          <v-icon size="34">mdi-progress-download</v-icon>
        </div>
        <div class="pycad-welcome__title">Loading data...</div>
        <v-progress-linear
          indeterminate
          rounded
          height="4"
          class="pycad-welcome__progress"
        />
      </template>
    </div>
  </div>
</template>

<style scoped>
.pycad-welcome {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 24px;
  background: radial-gradient(
    ellipse at center,
    #191919 0%,
    #111111 35%,
    #050505 100%
  );
  font-family: 'Inter', system-ui, sans-serif;
}

.pycad-welcome__dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: min(460px, 100%);
  padding: 48px 40px;
  border: 1.5px dashed var(--pycad-border, #333);
  border-radius: var(--pycad-radius, 14px);
  background: rgba(255, 255, 255, 0.02);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;
}

.pycad-welcome:hover .pycad-welcome__dropzone:not(.is-loading) {
  border-color: var(--pycad-primary-border, rgba(255, 153, 0, 0.45));
  background: rgba(255, 153, 0, 0.03);
  box-shadow: var(--pycad-shadow-glow, 0 0 18px rgba(255, 153, 0, 0.12));
}

.pycad-welcome__icon-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--pycad-primary-dim, rgba(255, 153, 0, 0.12));
  border: 1px solid var(--pycad-primary-border, rgba(255, 153, 0, 0.3));
  color: var(--pycad-primary, #ff9900);
  margin-bottom: 22px;
}

.pycad-welcome__icon-badge :deep(.v-icon) {
  color: var(--pycad-primary, #ff9900);
}

.pycad-welcome__icon-badge--pulse {
  animation: pycad-welcome-pulse 1.6s ease-in-out infinite;
}

@keyframes pycad-welcome-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(255, 153, 0, 0.25);
  }
  50% {
    box-shadow: 0 0 0 12px rgba(255, 153, 0, 0);
  }
}

.pycad-welcome__title {
  font-size: 19px;
  font-weight: 600;
  color: var(--pycad-text-primary, #fff);
  letter-spacing: 0.01em;
}

.pycad-welcome__subtitle {
  margin-top: 6px;
  font-size: 13.5px;
  color: var(--pycad-text-secondary, #a0a0a0);
}

.pycad-welcome__cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
  padding: 9px 22px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #111;
  background: var(--pycad-primary, #ff9900);
  border-radius: var(--pycad-radius-sm, 8px);
  transition:
    filter 0.15s ease,
    transform 0.15s ease;
}

.pycad-welcome__cta :deep(.v-icon) {
  color: #111;
}

.pycad-welcome:hover .pycad-welcome__cta {
  filter: brightness(1.08);
  transform: translateY(-1px);
}

.pycad-welcome__formats {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  margin-top: 26px;
}

.pycad-welcome__format-chip {
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: var(--pycad-text-secondary, #a0a0a0);
  background: var(--pycad-elevated, #161616);
  border: 1px solid var(--pycad-border, #282828);
  border-radius: 999px;
}

.pycad-welcome__privacy {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 20px;
  font-size: 11.5px;
  color: var(--pycad-text-muted, #666);
}

.pycad-welcome__privacy :deep(.v-icon) {
  color: var(--pycad-text-muted, #666);
}

.pycad-welcome__progress {
  width: 240px;
  margin-top: 20px;
  color: var(--pycad-primary, #ff9900);
}
</style>
